import React, { useCallback, useEffect, useState } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getAdminQuestionGroupByIdUrl, getAdminQuestionGetByIdUrl } from '../utils/UrlUtilities';
import QuestionAddForm from './QuestionAddForm';
import QuestionEditForm from './QuestionEditForm';

// Linear wizard for a passage's questions: pages through existing member questions (edit)
// and an "add new" step, with Back / Next navigation. From question #N you can step back to
// #N-1, and from the first question step back to the passage text (onEditPassage). Each
// question is persisted as it's saved (draft), so the list stays in sync via re-fetch.
export default function PassageQuestionWizard({ group, onExit, onEditPassage }) {
    const CallApi = useApiCaller();
    const [ids, setIds] = useState([]);          // ordered member question ids
    const [index, setIndex] = useState(0);        // 0..ids.length ; === ids.length => add-new
    const [current, setCurrent] = useState(null); // full question object for the edit step
    const [loading, setLoading] = useState(true);

    const groupDefaults = {
        level: group.level, division: group.division, unit: group.unit,
        subject: group.subject, exam: group.exam, year: group.year,
    };

    const fetchIds = useCallback(async () => {
        const res = await CallApi({ url: getAdminQuestionGroupByIdUrl(group.group_id), method: 'GET', token: true, payload: null });
        return res.success ? (res.data.questions || []).map(q => q.question_id) : [];
    }, [CallApi, group.group_id]);

    // On mount: load the member ids and start on the "add new" step (end of the list).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const list = await fetchIds();
            if (cancelled) return;
            setIds(list);
            setIndex(list.length);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [fetchIds]);

    // When pointing at an existing question, load it fully for the edit step.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (index >= ids.length) { setCurrent(null); return; }
            setLoading(true);
            const res = await CallApi({ url: getAdminQuestionGetByIdUrl(ids[index]), method: 'GET', token: true, payload: null });
            if (cancelled) return;
            setCurrent(res.success ? res.data : null);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [CallApi, index, ids]);

    const goBack = () => {
        if (index > 0) setIndex(index - 1);
        else onEditPassage();           // at the first question → back to the passage text modal
    };

    if (loading && index >= ids.length && ids.length === 0) {
        return <div className="text-center text-muted py-4">Loading passage…</div>;
    }

    // Editing an existing question #(index+1).
    if (index < ids.length) {
        if (!current) return <div className="text-center text-muted py-4">Loading question…</div>;
        return (
            <QuestionEditForm
                question={current}
                title={`Passage question #${index + 1} of ${ids.length}`}
                backLabel={index > 0 ? '◀ Previous question' : '◀ Back to passage text'}
                onBack={goBack}
                nextLabel={index + 1 < ids.length ? 'Next question ▶' : 'Add a new question ▶'}
                onNext={() => setIndex(index + 1)}
                onCancel={onExit}
                onSave={async () => {
                    // Saved edits — refresh ids and advance to the next question / add-new.
                    const list = await fetchIds();
                    setIds(list);
                    setIndex(Math.min(index + 1, list.length));
                }}
            />
        );
    }

    // Adding a new question at the end.
    return (
        <QuestionAddForm
            groupId={group.group_id}
            defaultLevel={groupDefaults.level || ''}
            defaultDivision={groupDefaults.division || ''}
            defaultUnit={groupDefaults.unit || ''}
            defaultSubject={groupDefaults.subject || ''}
            defaultExam={groupDefaults.exam || ''}
            defaultYear={groupDefaults.year || ''}
            passageStartCount={ids.length}
            title="Add Question to Passage"
            addAnotherLabel="Add Another Question to this Passage"
            saveLabel="Finish Creating the Passage Questions"
            cancelLabel="Discard Creating Passage Questions"
            backLabel={ids.length > 0 ? '◀ Previous question' : '◀ Back to passage text'}
            onBack={() => { if (ids.length > 0) setIndex(ids.length - 1); else onEditPassage(); }}
            onSaved={async () => { const list = await fetchIds(); setIds(list); setIndex(list.length); }}
            onCancel={onExit}
            onSave={onExit}
        />
    );
}
